import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { APP_SETTINGS } from '../config/appSettings'
import { Icon } from './Icon'

interface SearchSelectionBarProps {
  count: number
  onBump: () => void
  onMerge?: () => void
  onTags?: () => void
  onExport?: () => void
  onCopy: () => void
  onDismiss: () => void
  onTrash: () => void
  open: boolean
}

export function SearchSelectionBar({
  count,
  onBump,
  onCopy,
  onMerge,
  onTags,
  onExport,
  onDismiss,
  onTrash,
  open,
}: SearchSelectionBarProps) {
  const [mounted, setMounted] = useState(open)
  const [displayedCount, setDisplayedCount] = useState(count)
  const moreRef = useRef<HTMLDetailsElement>(null)
  const moreTriggerRef = useRef<HTMLElement>(null)

  useLayoutEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    if (!mounted) return
    const timer = window.setTimeout(
      () => setMounted(false),
      APP_SETTINGS.interactions.searchSelectionBarAnimationMs,
    )
    return () => window.clearTimeout(timer)
  }, [mounted, open])

  useEffect(() => {
    if (open) setDisplayedCount(count)
  }, [count, open])

  useEffect(() => {
    const closeMoreOnOutsidePress = (event: PointerEvent) => {
      const menu = moreRef.current
      if (menu?.open && event.target instanceof Node && !menu.contains(event.target)) {
        menu.open = false
      }
    }
    document.addEventListener('pointerdown', closeMoreOnOutsidePress)
    return () => document.removeEventListener('pointerdown', closeMoreOnOutsidePress)
  }, [])

  if (!mounted) return null
  const closing = !open
  const closeMore = () => {
    if (moreRef.current) moreRef.current.open = false
  }
  const runMoreAction = (action: (() => void) | undefined) => {
    closeMore()
    action?.()
  }

  return (
    <aside
      className="search-selection-bar visual-viewport-bottom"
      data-presence={closing ? 'closing' : 'open'}
      aria-hidden={closing || undefined}
      aria-label="Selected note actions"
      style={{
        '--search-selection-bar-animation-duration': `${APP_SETTINGS.interactions.searchSelectionBarAnimationMs}ms`,
      } as CSSProperties}
    >
      <div className="search-selection-bar__header">
        <span className="search-selection-bar__count">{displayedCount} selected</span>
        <button className="btn btn-ghost search-selection-bar__dismiss" type="button" onClick={onDismiss} aria-label="Dismiss multi-select" title="Dismiss"><Icon name="close" /></button>
      </div>
      <div className="search-selection-bar__actions" role="group" aria-label="Selected note actions">
        <button className="btn btn-ghost" type="button" onClick={onCopy} disabled={displayedCount === 0} aria-label="Copy selected notes" title="Copy selected notes"><Icon name="copy" /><span>Copy</span></button>
        <button className="btn btn-ghost" type="button" onClick={onBump} disabled={displayedCount === 0} aria-label="Bump selected notes" title="Bump selected notes"><Icon name="bump" /><span>Bump</span></button>
        {onTags && <button className="btn btn-ghost" type="button" onClick={onTags} disabled={displayedCount === 0} aria-label="Edit tags on selected notes" title="Edit tags"><Icon name="tag" /><span>Tags</span></button>}
        {(onTags || onMerge || onExport) && <details
          ref={moreRef}
          className="dropdown dropdown-top dropdown-end search-selection-bar__more"
          onKeyDown={(event) => {
            if (event.key !== 'Escape') return
            event.preventDefault()
            event.stopPropagation()
            closeMore()
            moreTriggerRef.current?.focus()
          }}
        >
          <summary ref={moreTriggerRef} className="btn btn-ghost" role="button" aria-label="More selected note actions" title="More actions"><Icon name="more" /><span>More</span></summary>
          <ul className="menu dropdown-content search-selection-bar__more-menu" aria-label="More selected note actions">
            {onTags && <li><button type="button" onClick={() => runMoreAction(onTags)} disabled={displayedCount === 0} aria-label="Edit tags on selected notes"><Icon name="tag" /><span>Tags</span></button></li>}
            {onMerge && <li><button type="button" onClick={() => runMoreAction(onMerge)} disabled={displayedCount < 2} aria-label="Merge selected notes" title={displayedCount < 2 ? 'Select at least two notes to merge' : 'Merge selected notes'}><Icon name="merge" /><span>Merge</span></button></li>}
            {onExport && <li><button type="button" onClick={() => runMoreAction(onExport)} disabled={displayedCount === 0} aria-label="Export selected notes"><Icon name="arrow-down" /><span>Export</span></button></li>}
          </ul>
        </details>}
        <button className="btn btn-ghost search-selection-bar__trash" type="button" onClick={onTrash} disabled={displayedCount === 0} aria-label="Move selected notes to trash" title="Move selected notes to trash"><Icon name="trash" /><span>Trash</span></button>
      </div>
    </aside>
  )
}
